import React from "react";
import { Landmark, Globe, FileCheck, Network, AlertTriangle, Wallet } from "lucide-react";
import { Hero, Section, Sub, Callout, NumberedItem, Bold } from "./_shared";
import { useT } from "@/i18n";

export default function BankingPage() {
  const { lang } = useT();
  return lang === "en" ? <BankingEN /> : <BankingFR />;
}

// =============================================================
// FR
// =============================================================
function BankingFR() {
  return (
    <div data-testid="view-banking" className="max-w-5xl">
      <Hero
        moduleNum="02"
        eyebrowLabel="Module"
        icon={Landmark}
        italic="Banking"
        accent="& comptes internationaux"
        description="Comprendre le système bancaire international est indispensable pour un intermédiaire professionnel. C'est ce qui vous permet de structurer correctement les paiements, de protéger vos commissions, et d'optimiser votre fiscalité selon votre profil."
      />

      <Section eyebrow="Juridictions" title="Taux d'intérêt sur comptes étrangers" index="2.1">
        <p>
          Ces chiffres sont indicatifs et basés sur les données 2024-2025. Ils varient selon les banques, les montants
          déposés, et les politiques des banques centrales. Vérifiez toujours directement avec l'institution bancaire concernée.
        </p>

        <Sub title="Émirats Arabes Unis (UAE) 🇦🇪">
          <p><Bold>Taux sur dépôts en USD :</Bold> 0,5 % à 3,5 % annuel selon les banques et les montants.</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Pas d'impôt sur les revenus d'intérêts. Pas de retenue à la source. Hub
            idéal pour les intermédiaires actifs au Moyen-Orient et en Afrique. Banques recommandées : Emirates NBD,
            Mashreq, ADCB, First Abu Dhabi Bank. RAK Bank est souvent plus flexible pour les non-résidents.
          </p>
        </Sub>

        <Sub title="Suisse 🇨🇭">
          <p><Bold>Taux sur CHF :</Bold> 0 % à 1,2 % annuel. <Bold>Taux sur USD :</Bold> jusqu'à 2,5 % en compte épargne premium.</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Stabilité absolue, confidentialité historique (bien qu'encadrée par les
            accords OCDE depuis 2018). Frais de tenue de compte élevés : 200 à 500 CHF par an minimum. Banques recommandées :
            UBS, Credit Suisse (désormais intégré à UBS), Pictet, Julius Bär pour les profils patrimoniaux.
          </p>
        </Sub>

        <Sub title="Singapour 🇸🇬">
          <p><Bold>Taux sur SGD :</Bold> 1 % à 3,5 % annuel. <Bold>Taux sur USD :</Bold> jusqu'à 4 % dans certaines banques (DBS, OCBC, UOB avec conditions).</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Hub financier asiatique incontournable. Pas d'impôt sur les plus-values.
            Réglementation très solide. Ouverture de compte possible en non-résident mais avec justificatifs sérieux.
            Idéal pour les flux avec l'Asie.
          </p>
        </Sub>

        <Sub title="Îles Caïmans 🇰🇾">
          <p><Bold>Taux sur USD :</Bold> 1 % à 3 % annuel.</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Aucune taxe locale. Utilisé principalement pour des structures holding et
            des SPV (Special Purpose Vehicles). Accès difficile pour les non-résidents sans structure juridique locale
            établie. Sous surveillance internationale croissante (liste OCDE des juridictions non coopératives).
          </p>
        </Sub>

        <Sub title="Maurice (Mauritius) 🇲🇺">
          <p><Bold>Taux sur USD :</Bold> jusqu'à 4,5 % annuel.</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Pas d'imposition sur les revenus étrangers. Conventions fiscales avec 46
            pays dont la France. Très utilisé pour les flux commerciaux avec l'Afrique subsaharienne. AfrAsia Bank est
            particulièrement ouverte aux non-résidents avec profil international.
          </p>
        </Sub>

        <Sub title="Géorgie (Caucase) 🇬🇪">
          <p><Bold>Taux sur USD :</Bold> 5 % à 8 % annuel selon les banques (TBC Bank, Bank of Georgia).</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Ouverture de compte très accessible, même en non-résident. Économie en
            croissance rapide. Fiscalité avantageuse pour les non-résidents. Moins de prestige que les autres juridictions
            mais pratique pour des flux réguliers.
          </p>
        </Sub>

        <Sub title="Turquie 🇹🇷">
          <p><Bold>Taux sur TRY (lire turque) :</Bold> jusqu'à 8 %, mais risque de change très élevé (la lire a perdu plus de 80 % de sa valeur sur 5 ans). <Bold>Taux sur USD :</Bold> 3 % à 4 %.</p>
          <p>À considérer avec prudence compte tenu de l'instabilité monétaire.</p>
        </Sub>

        <Sub title="Luxembourg 🇱🇺">
          <p><Bold>Taux sur EUR :</Bold> 1 % à 2,8 % annuel.</p>
          <p>
            <Bold>Caractéristiques clés :</Bold> Centre financier européen majeur. Accès au réseau de conventions fiscales
            de l'Union Européenne. Idéal pour les structures holding opérant en zone Euro. BNP Paribas Luxembourg, ING
            Luxembourg, Banque de Luxembourg sont les principales références.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Procédure" title="Ouvrir un compte à l'étranger en tant que non-résident" index="2.2">
        <Sub title="Documents généralement requis">
          <ul className="space-y-2">
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Passeport valide (copie certifiée conforme dans certains pays, parfois apostillée)</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Preuve d'adresse récente de moins de 3 mois (facture d'eau, d'électricité, ou avis d'imposition — les relevés bancaires sont souvent acceptés)</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Formulaire KYC (Know Your Customer) complet incluant la déclaration de la source des fonds</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Référence bancaire de votre banque actuelle sur papier en-tête officiel (certaines banques l'exigent impérativement)</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Description de l'activité professionnelle ou plan d'affaires pour les comptes business</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Apostille sur les documents officiels si requis par la législation du pays d'accueil</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Parfois : déclaration de patrimoine ou justificatif de revenus (relevés de comptes sur 6 mois)</span></li>
          </ul>
        </Sub>

        <Sub title="Ouvrir un compte aux UAE (Dubai) — Étapes détaillées">
          <div className="space-y-4">
            <NumberedItem n="01" title="Choisir la banque">
              <p>Emirates NBD (la plus grande, bon service international), Mashreq (bonne option pour les non-résidents),
              ADCB (Abu Dhabi Commercial Bank), ou RAK Bank (Ras Al Khaimah Bank, souvent la plus flexible pour les profils non-résidents).</p>
            </NumberedItem>
            <NumberedItem n="02" title="Prendre rendez-vous en agence">
              <p>Les ouvertures 100 % en ligne restent rares pour les non-résidents. Il faut se présenter physiquement au
              moins une fois. Certaines banques proposent une pré-qualification en ligne suivie d'une visite physique.</p>
            </NumberedItem>
            <NumberedItem n="03" title="Préparer les documents">
              <p>Passeport, visa UAE si vous en avez un, preuve d'adresse dans votre pays de résidence, sources de revenus
              documentées (contrats de commission, relevés de compte, déclarations fiscales).</p>
            </NumberedItem>
            <NumberedItem n="04" title="Premier dépôt minimum">
              <p>Généralement entre AED 3 000 (compte personal standard) et AED 50 000 pour un compte business premium.
              RAK Bank accepte parfois des dépôts initiaux plus faibles.</p>
            </NumberedItem>
            <NumberedItem n="05" title="Délai d'ouverture">
              <p>De 3 jours à 15 jours ouvrables selon la banque et la complexité du profil.</p>
            </NumberedItem>
            <NumberedItem n="06" title="Pour un compte business">
              <p>Il faut généralement une licence d'activité UAE, soit en freezone (DMCC, DAFZA, JAFZA, Dubai South, etc.)
              soit en mainland. La freezone est souvent suffisante pour un intermédiaire en commerce international.</p>
            </NumberedItem>
          </div>
        </Sub>

        <Sub title="Ouvrir un compte à Maurice — Étapes détaillées">
          <div className="space-y-4">
            <NumberedItem n="01" title="Identifier la banque">
              <p>Mauritius Commercial Bank (MCB) est la plus grande et la plus réputée. AfrAsia Bank est la plus ouverte
              aux profils internationaux et non-résidents. SBM Bank (State Bank of Mauritius) est une troisième option solide.</p>
            </NumberedItem>
            <NumberedItem n="02" title="Documents requis">
              <p>Passeport, preuve d'adresse, lettre de référence bancaire sur papier à en-tête officiel de votre banque
              actuelle, questionnaire KYC très détaillé avec source of wealth et source of funds, business plan si compte business.</p>
            </NumberedItem>
            <NumberedItem n="03" title="Dépôt minimum">
              <p>Généralement USD 1 000 à USD 10 000 selon le type de compte.</p>
            </NumberedItem>
            <NumberedItem n="04" title="Délai">
              <p>2 à 4 semaines en moyenne.</p>
            </NumberedItem>
            <NumberedItem n="05" title="Avantage clé">
              <p>La convention fiscale France-Maurice permet d'éliminer ou de réduire la double imposition sur les revenus
              perçus depuis Maurice par un résident français, sous conditions.</p>
            </NumberedItem>
          </div>
        </Sub>
      </Section>

      <Section eyebrow="Infrastructure" title="Le système SWIFT et les banques correspondantes" index="2.3">
        <Sub title="Qu'est-ce que SWIFT ?">
          <p>
            SWIFT (Society for Worldwide Interbank Financial Telecommunication) est le réseau de messagerie sécurisée
            utilisé par <Bold>plus de 11 000 institutions financières dans plus de 200 pays</Bold> pour s'envoyer des
            instructions de paiement.
          </p>
          <Callout icon={Network} title="Important à comprendre">
            <p>
              SWIFT n'est PAS un système de virement en lui-même. C'est un <Bold>réseau de communication sécurisé</Bold>.
              Les virements réels sont effectués par les banques à travers leurs systèmes nationaux et internationaux.
              SWIFT est le langage commun qu'elles utilisent pour se parler.
            </p>
          </Callout>
        </Sub>

        <Sub title="Structure d'un code SWIFT / BIC">
          <p>Un code SWIFT (aussi appelé BIC) comprend :</p>
          <ul className="space-y-2">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>4 premières lettres :</Bold> code de la banque. Exemple : BNPA = BNP Paribas, DEUT = Deutsche Bank, HSBC = HSBC</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>2 lettres suivantes :</Bold> code pays ISO. FR = France, AE = Émirats, GB = Royaume-Uni, SG = Singapour</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>2 caractères suivants :</Bold> code de localisation (ville ou zone géographique)</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>3 derniers caractères (optionnels) :</Bold> code de l'agence spécifique</span></li>
          </ul>
          <p>
            <Bold>Exemple complet :</Bold> <span className="font-mono text-[#D4AF37]">BNPAFRPPXXX</span> = BNP Paribas (BNPA)
            + France (FR) + Paris (PP) + siège social (XXX)
          </p>
        </Sub>

        <Sub title="Les banques correspondantes (Correspondent Banks)">
          <p>
            Lorsque deux banques n'ont pas de relation directe entre elles, elles passent par une banque correspondante —
            une tierce banque qui a des accords bilatéraux avec les deux. C'est crucial dans les deals internationaux car :
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Les délais de virement sont allongés de 1 à 3 jours supplémentaires par banque correspondante dans la chaîne.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Des frais de correspondant (nostro fees ou correspondent banking fees) sont prélevés à chaque étape.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Il peut y avoir 2 ou 3 banques correspondantes dans une chaîne de paiement unique.</span></li>
          </ul>
          <p>
            <Bold>Exemple de chaîne de paiement :</Bold> Votre banque française (BNP Paris) → Banque correspondante européenne
            (Deutsche Bank Francfort) → Banque correspondante locale (Standard Bank Johannesburg) → Banque du bénéficiaire
            (Absa Bank Johannesburg). Dans cet exemple, 2 banques correspondantes sont impliquées.
          </p>
        </Sub>

        <Sub title="Frais de virement international — Détail complet">
          <ul className="space-y-3">
            <li><Bold>Frais SWIFT émetteur</Bold> (votre banque qui envoie) : USD 20 à USD 80 par transaction selon la banque et le montant.</li>
            <li><Bold>Frais banque correspondante :</Bold> USD 10 à USD 50 par étape, et il peut y avoir 2 ou 3 étapes dans la chaîne. Total possible : USD 30 à USD 150 en frais de correspondant uniquement.</li>
            <li><Bold>Frais de change</Bold> (si les devises sont différentes) : 0,5 % à 2,5 % du montant converti. C'est souvent le poste de coût le plus important sur les petits virements.</li>
            <li><Bold>Frais de réception</Bold> (banque bénéficiaire) : USD 5 à USD 25 selon les banques, ou parfois inclus dans le service.</li>
            <li><Bold>Frais de confirmation MT103</Bold> (le reçu officiel du virement) : USD 50 à USD 200 selon la banque, à demander spécifiquement.</li>
          </ul>
          <p><Bold>Total estimatif sur un virement de USD 100 000 :</Bold> entre USD 100 et USD 400 selon les routes bancaires utilisées.</p>
          <Callout icon={FileCheck} title="Conseil important">
            Toujours demander la <Bold>confirmation MT103</Bold> après chaque virement. Ce document est l'équivalent d'un
            reçu de virement SWIFT — il prouve que le virement a bien été initié depuis la banque émettrice. Il est
            indispensable en cas de litige ou de non-réception.
          </Callout>
        </Sub>
      </Section>

      <Section eyebrow="Légalité" title="Compte offshore vs compte étranger classique" index="2.4">
        <p>Beaucoup de gens confondent ces deux notions, avec des conséquences parfois légales graves.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <Wallet className="h-5 w-5 text-[#D4AF37]" />
            <h4 className="font-serif text-xl text-white mt-3">Compte étranger classique</h4>
            <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
              C'est simplement un compte bancaire personnel ou professionnel ouvert dans un pays étranger. Exemple : un
              Français qui ouvre un compte chez Emirates NBD à Dubai. Ce compte est <Bold>légal</Bold>, doit être déclaré
              au fisc français (formulaire 3916), et est soumis aux règles fiscales françaises selon la convention bilatérale
              applicable.
            </p>
          </div>
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <Globe className="h-5 w-5 text-[#D4AF37]" />
            <h4 className="font-serif text-xl text-white mt-3">Compte offshore</h4>
            <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
              Terme plus large qui désigne généralement un compte rattaché à une <Bold>structure juridique (société)</Bold>
              enregistrée dans une juridiction à faible fiscalité ou réglementation allégée. Exemple : une LLC enregistrée
              aux îles Caïmans avec un compte bancaire à la Cayman National Bank. Ce type de structure est légal mais soumis
              à des obligations déclaratives strictes et sous surveillance internationale renforcée.
            </p>
          </div>
        </div>

        <Sub title="Obligations déclaratives en France">
          <p>
            Depuis 2013, tout compte bancaire détenu à l'étranger doit être déclaré au fisc français via le
            <Bold> formulaire 3916</Bold>, joint à la déclaration de revenus annuelle.
          </p>
          <Callout icon={AlertTriangle} title="Sanctions">
            <ul className="space-y-2">
              <li className="flex gap-2"><span className="text-[#FF3B30]">▸</span><span><Bold>1 500 €</Bold> par compte non déclaré</span></li>
              <li className="flex gap-2"><span className="text-[#FF3B30]">▸</span><span><Bold>10 000 €</Bold> si le compte est situé dans un pays ou territoire non coopératif (liste noire OCDE)</span></li>
              <li className="flex gap-2"><span className="text-[#FF3B30]">▸</span><span>En cas de fraude avérée, les amendes peuvent atteindre <Bold>80 % des avoirs non déclarés</Bold>.</span></li>
            </ul>
          </Callout>
          <p className="italic text-[#F5F5DC]">
            La règle est simple : le compte étranger en lui-même est légal. Ce qui est illégal, c'est de ne pas le déclarer
            et de ne pas intégrer les revenus générés dans sa base taxable française.
          </p>
        </Sub>
      </Section>
    </div>
  );
}

// =============================================================
// EN
// =============================================================
function BankingEN() {
  return (
    <div data-testid="view-banking" className="max-w-5xl">
      <Hero
        moduleNum="02"
        eyebrowLabel="Module"
        icon={Landmark}
        italic="Banking"
        accent="& international accounts"
        description="Understanding the international banking system is essential for a professional intermediary. It's what lets you structure payments correctly, protect your commissions, and optimise your tax position based on your profile."
      />

      <Section eyebrow="Jurisdictions" title="Interest rates on foreign accounts" index="2.1">
        <p>
          These figures are indicative and based on 2024–2025 data. They vary depending on banks, deposit amounts, and
          central bank policies. Always verify directly with the institution concerned.
        </p>

        <Sub title="United Arab Emirates (UAE) 🇦🇪">
          <p><Bold>Rates on USD deposits:</Bold> 0.5% to 3.5% per year depending on banks and amounts.</p>
          <p>
            <Bold>Key features:</Bold> No tax on interest income. No withholding tax. Ideal hub for intermediaries active
            in the Middle East and Africa. Recommended banks: Emirates NBD, Mashreq, ADCB, First Abu Dhabi Bank. RAK Bank
            is often the most flexible for non-residents.
          </p>
        </Sub>

        <Sub title="Switzerland 🇨🇭">
          <p><Bold>CHF rates:</Bold> 0% to 1.2% per year. <Bold>USD rates:</Bold> up to 2.5% on premium savings accounts.</p>
          <p>
            <Bold>Key features:</Bold> Absolute stability, historical confidentiality (now framed by OECD agreements
            since 2018). High account-keeping fees: 200 to 500 CHF per year minimum. Recommended banks: UBS, Credit Suisse
            (now integrated into UBS), Pictet, Julius Bär for wealth profiles.
          </p>
        </Sub>

        <Sub title="Singapore 🇸🇬">
          <p><Bold>SGD rates:</Bold> 1% to 3.5% per year. <Bold>USD rates:</Bold> up to 4% in some banks (DBS, OCBC, UOB under conditions).</p>
          <p>
            <Bold>Key features:</Bold> Unavoidable Asian financial hub. No capital gains tax. Very strong regulation.
            Account opening possible as a non-resident but with serious supporting documents. Ideal for flows with Asia.
          </p>
        </Sub>

        <Sub title="Cayman Islands 🇰🇾">
          <p><Bold>USD rates:</Bold> 1% to 3% per year.</p>
          <p>
            <Bold>Key features:</Bold> No local tax. Used mainly for holding structures and SPVs (Special Purpose
            Vehicles). Difficult access for non-residents without an established local legal structure. Under growing
            international scrutiny (OECD list of non-cooperative jurisdictions).
          </p>
        </Sub>

        <Sub title="Mauritius 🇲🇺">
          <p><Bold>USD rates:</Bold> up to 4.5% per year.</p>
          <p>
            <Bold>Key features:</Bold> No taxation on foreign-source income. Tax treaties with 46 countries including
            France. Heavily used for trade flows with Sub-Saharan Africa. AfrAsia Bank is particularly open to
            non-residents with an international profile.
          </p>
        </Sub>

        <Sub title="Georgia (Caucasus) 🇬🇪">
          <p><Bold>USD rates:</Bold> 5% to 8% per year depending on banks (TBC Bank, Bank of Georgia).</p>
          <p>
            <Bold>Key features:</Bold> Very accessible account opening, even as a non-resident. Fast-growing economy.
            Favorable taxation for non-residents. Less prestige than other jurisdictions but practical for regular flows.
          </p>
        </Sub>

        <Sub title="Turkey 🇹🇷">
          <p><Bold>TRY (Turkish lira) rates:</Bold> up to 8%, but very high FX risk (the lira has lost over 80% of its value over 5 years). <Bold>USD rates:</Bold> 3% to 4%.</p>
          <p>To consider with caution given monetary instability.</p>
        </Sub>

        <Sub title="Luxembourg 🇱🇺">
          <p><Bold>EUR rates:</Bold> 1% to 2.8% per year.</p>
          <p>
            <Bold>Key features:</Bold> Major European financial center. Access to the EU network of tax treaties. Ideal
            for holding structures operating in the Eurozone. BNP Paribas Luxembourg, ING Luxembourg, Banque de
            Luxembourg are the main references.
          </p>
        </Sub>
      </Section>

      <Section eyebrow="Procedure" title="Opening an account abroad as a non-resident" index="2.2">
        <Sub title="Documents typically required">
          <ul className="space-y-2">
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Valid passport (certified copy in some countries, sometimes apostilled)</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Recent proof of address less than 3 months old (utility bill, electricity bill, or tax notice — bank statements are often accepted)</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Complete KYC (Know Your Customer) form including a declaration of the source of funds</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Bank reference letter from your current bank on official letterhead (some banks demand it strictly)</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Description of professional activity or business plan for business accounts</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Apostille on official documents if required by the host country's legislation</span></li>
            <li className="flex gap-3"><FileCheck className="h-4 w-4 text-[#D4AF37] shrink-0 mt-1" /><span>Sometimes: declaration of wealth or income proof (6 months of bank statements)</span></li>
          </ul>
        </Sub>

        <Sub title="Opening an account in the UAE (Dubai) — Detailed steps">
          <div className="space-y-4">
            <NumberedItem n="01" title="Choose the bank">
              <p>Emirates NBD (the largest, good international service), Mashreq (good option for non-residents), ADCB
              (Abu Dhabi Commercial Bank), or RAK Bank (Ras Al Khaimah Bank, often the most flexible for non-resident
              profiles).</p>
            </NumberedItem>
            <NumberedItem n="02" title="Book a branch appointment">
              <p>100% online openings remain rare for non-residents. You must show up in person at least once. Some banks
              offer online pre-qualification followed by a physical visit.</p>
            </NumberedItem>
            <NumberedItem n="03" title="Prepare the documents">
              <p>Passport, UAE visa if you have one, proof of address in your country of residence, documented income
              sources (commission contracts, account statements, tax filings).</p>
            </NumberedItem>
            <NumberedItem n="04" title="Minimum first deposit">
              <p>Generally between AED 3,000 (standard personal account) and AED 50,000 for a premium business account.
              RAK Bank sometimes accepts lower initial deposits.</p>
            </NumberedItem>
            <NumberedItem n="05" title="Opening timeline">
              <p>From 3 days to 15 business days depending on the bank and complexity of the profile.</p>
            </NumberedItem>
            <NumberedItem n="06" title="For a business account">
              <p>You generally need a UAE activity license, either in a freezone (DMCC, DAFZA, JAFZA, Dubai South, etc.)
              or mainland. The freezone is often sufficient for an international trade intermediary.</p>
            </NumberedItem>
          </div>
        </Sub>

        <Sub title="Opening an account in Mauritius — Detailed steps">
          <div className="space-y-4">
            <NumberedItem n="01" title="Identify the bank">
              <p>Mauritius Commercial Bank (MCB) is the largest and most reputable. AfrAsia Bank is the most open to
              international and non-resident profiles. SBM Bank (State Bank of Mauritius) is a solid third option.</p>
            </NumberedItem>
            <NumberedItem n="02" title="Required documents">
              <p>Passport, proof of address, bank reference letter on official letterhead from your current bank, very
              detailed KYC questionnaire with source of wealth and source of funds, business plan if business account.</p>
            </NumberedItem>
            <NumberedItem n="03" title="Minimum deposit">
              <p>Generally USD 1,000 to USD 10,000 depending on the account type.</p>
            </NumberedItem>
            <NumberedItem n="04" title="Timeline">
              <p>2 to 4 weeks on average.</p>
            </NumberedItem>
            <NumberedItem n="05" title="Key advantage">
              <p>The France-Mauritius tax treaty allows elimination or reduction of double taxation on income received
              from Mauritius by a French resident, under conditions.</p>
            </NumberedItem>
          </div>
        </Sub>
      </Section>

      <Section eyebrow="Infrastructure" title="The SWIFT system and correspondent banks" index="2.3">
        <Sub title="What is SWIFT?">
          <p>
            SWIFT (Society for Worldwide Interbank Financial Telecommunication) is the secure messaging network used by
            <Bold> more than 11,000 financial institutions across more than 200 countries</Bold> to send each other
            payment instructions.
          </p>
          <Callout icon={Network} title="Important to understand">
            <p>
              SWIFT is NOT a transfer system in itself. It's a <Bold>secure communication network</Bold>. Actual transfers
              are made by banks through their domestic and international systems. SWIFT is the common language they use to
              talk to each other.
            </p>
          </Callout>
        </Sub>

        <Sub title="Structure of a SWIFT / BIC code">
          <p>A SWIFT code (also called BIC) consists of:</p>
          <ul className="space-y-2">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>First 4 letters:</Bold> bank code. Example: BNPA = BNP Paribas, DEUT = Deutsche Bank, HSBC = HSBC</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Next 2 letters:</Bold> ISO country code. FR = France, AE = UAE, GB = United Kingdom, SG = Singapore</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Next 2 characters:</Bold> location code (city or geographic zone)</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span><Bold>Last 3 characters (optional):</Bold> specific branch code</span></li>
          </ul>
          <p>
            <Bold>Full example:</Bold> <span className="font-mono text-[#D4AF37]">BNPAFRPPXXX</span> = BNP Paribas (BNPA)
            + France (FR) + Paris (PP) + headquarters (XXX)
          </p>
        </Sub>

        <Sub title="Correspondent Banks">
          <p>
            When two banks have no direct relationship, they go through a correspondent bank — a third bank with bilateral
            agreements with both. This is crucial in international deals because:
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Transfer times are extended by 1 to 3 extra days for each correspondent bank in the chain.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>Correspondent fees (nostro fees or correspondent banking fees) are charged at each step.</span></li>
            <li className="flex gap-3"><span className="text-[#D4AF37] mt-2">▸</span><span>There can be 2 or 3 correspondent banks in a single payment chain.</span></li>
          </ul>
          <p>
            <Bold>Example payment chain:</Bold> Your French bank (BNP Paris) → European correspondent bank (Deutsche Bank
            Frankfurt) → Local correspondent bank (Standard Bank Johannesburg) → Beneficiary bank (Absa Bank
            Johannesburg). In this example, 2 correspondent banks are involved.
          </p>
        </Sub>

        <Sub title="International transfer fees — Full breakdown">
          <ul className="space-y-3">
            <li><Bold>Issuing SWIFT fees</Bold> (your sending bank): USD 20 to USD 80 per transaction depending on bank and amount.</li>
            <li><Bold>Correspondent bank fees:</Bold> USD 10 to USD 50 per step, and there can be 2 or 3 steps in the chain. Possible total: USD 30 to USD 150 in correspondent fees alone.</li>
            <li><Bold>FX fees</Bold> (if currencies differ): 0.5% to 2.5% of the converted amount. This is often the largest cost item on small transfers.</li>
            <li><Bold>Receiving fees</Bold> (beneficiary bank): USD 5 to USD 25 depending on banks, sometimes included in the service.</li>
            <li><Bold>MT103 confirmation fees</Bold> (the official transfer receipt): USD 50 to USD 200 depending on the bank — request specifically.</li>
          </ul>
          <p><Bold>Estimated total on a USD 100,000 transfer:</Bold> between USD 100 and USD 400 depending on banking routes used.</p>
          <Callout icon={FileCheck} title="Important advice">
            Always request the <Bold>MT103 confirmation</Bold> after each transfer. This document is the equivalent of a
            SWIFT transfer receipt — it proves the transfer was indeed initiated from the issuing bank. It is essential
            in case of dispute or non-receipt.
          </Callout>
        </Sub>
      </Section>

      <Section eyebrow="Legality" title="Offshore account vs standard foreign account" index="2.4">
        <p>Many people confuse these two notions, sometimes with serious legal consequences.</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <Wallet className="h-5 w-5 text-[#D4AF37]" />
            <h4 className="font-serif text-xl text-white mt-3">Standard foreign account</h4>
            <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
              Simply a personal or business bank account opened in a foreign country. Example: a French citizen opening
              an account at Emirates NBD in Dubai. This account is <Bold>legal</Bold>, must be declared to the French tax
              authority (Form 3916), and is subject to French tax rules under the applicable bilateral treaty.
            </p>
          </div>
          <div className="border border-white/10 bg-[#0c0c0c] p-6">
            <Globe className="h-5 w-5 text-[#D4AF37]" />
            <h4 className="font-serif text-xl text-white mt-3">Offshore account</h4>
            <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">
              Broader term that generally refers to an account attached to a <Bold>legal entity (company)</Bold>
              registered in a jurisdiction with low taxation or light regulation. Example: a Cayman Islands LLC with a
              bank account at Cayman National Bank. This type of structure is legal but subject to strict reporting
              obligations and reinforced international scrutiny.
            </p>
          </div>
        </div>

        <Sub title="French reporting obligations">
          <p>
            Since 2013, every bank account held abroad must be declared to the French tax authority via
            <Bold> Form 3916</Bold>, attached to the annual income tax return.
          </p>
          <Callout icon={AlertTriangle} title="Penalties">
            <ul className="space-y-2">
              <li className="flex gap-2"><span className="text-[#FF3B30]">▸</span><span><Bold>€1,500</Bold> per undeclared account</span></li>
              <li className="flex gap-2"><span className="text-[#FF3B30]">▸</span><span><Bold>€10,000</Bold> if the account is in a non-cooperative country or territory (OECD blacklist)</span></li>
              <li className="flex gap-2"><span className="text-[#FF3B30]">▸</span><span>In case of proven fraud, fines can reach <Bold>80% of undeclared assets</Bold>.</span></li>
            </ul>
          </Callout>
          <p className="italic text-[#F5F5DC]">
            The rule is simple: the foreign account itself is legal. What is illegal is failing to declare it and failing
            to integrate the income generated into your French taxable base.
          </p>
        </Sub>
      </Section>
    </div>
  );
}
